import { Pill } from '@/components/ui/pill';
import { cn } from '@/lib/utils';
import type { EmployeeDirectoryAssignment } from '@/types/database.types';

import { seniorityLabels } from '../schemas/position.schema';

interface Props {
  assignments: EmployeeDirectoryAssignment[];
  /** Limita a las N primeras (resto se resume como "+N"). */
  max?: number;
  className?: string;
}

/**
 * Render compacto de áreas+cargos del empleado. El primario va primero y
 * con tono brand. El resto neutro.
 */
export function EmployeeBadges({ assignments, max, className }: Props) {
  if (assignments.length === 0) {
    return (
      <span className="text-fg-dim font-mono text-micro">
        sin asignación
      </span>
    );
  }

  const shown = typeof max === 'number' ? assignments.slice(0, max) : assignments;
  const extra =
    typeof max === 'number' ? Math.max(0, assignments.length - max) : 0;

  return (
    <div className={cn('flex flex-wrap items-center gap-1.5', className)}>
      {shown.map((a) => {
        const label = `${a.position_short ?? a.position_name} · ${a.area_name}`;
        const color = a.area_color ?? undefined;
        return (
          <span
            key={a.assignment_id}
            className="inline-flex items-center gap-1.5 rounded-md border border-[var(--border)] bg-[var(--surface-1)] px-2 py-0.5 text-caption leading-none"
            title={`${a.position_name} en ${a.area_name}${a.is_primary ? ' (principal)' : ''}`}
            style={
              color
                ? {
                    borderColor: `${color}55`,
                    background: `${color}11`,
                  }
                : undefined
            }
          >
            {color && (
              <span
                aria-hidden
                className="h-2 w-2 rounded-full"
                style={{ background: color }}
              />
            )}
            <span className="font-medium">{label}</span>
            {a.is_primary && (
              <span className="text-fg-dim text-micro">
                principal
              </span>
            )}
            <span className="text-fg-dim text-micro">
              {seniorityLabels[a.seniority]}
            </span>
          </span>
        );
      })}
      {extra > 0 && <Pill>+{extra}</Pill>}
    </div>
  );
}
